import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import { listModels } from '../services/models'
import { AppSettings } from '../App'
import {
  Sparkles,
  Copy,
  CornerDownLeft,
  CornerDownRight,
  AlertCircle,
  X,
  ChevronDown,
  ChevronRight,
  Check,
  RefreshCw,
  Plus,
  History,
  ListTree,
  MessageCircleQuestion,
  PenLine,
  Bug,
  Wrench,
  Brain,
  Pencil,
  Eye
} from 'lucide-react'
import { IconButton } from '@astryxdesign/core/IconButton'
import { HStack } from '@astryxdesign/core/HStack'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import {
  ChatMessageList,
  ChatMessage,
  ChatMessageBubble,
  ChatComposer,
  ChatComposerInput,
  type ChatComposerTrigger,
  ChatMessageMetadata
} from '@astryxdesign/core/Chat'
import {
  createStaticSource,
  TypeaheadItem,
  type SearchableItem
} from '@astryxdesign/core/Typeahead'
import { Timestamp } from '@astryxdesign/core/Timestamp'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { parseMessageBlocks } from '../utils/draftParser'
import { buildExecutePlanPrompt } from '../lib/planExecution'
import { marked } from 'marked'
import posthog from 'posthog-js'
import { createId } from '../lib/id'
import { markdownToManuscriptHtml } from '../lib/manuscriptHtml'
import { applyAgentEvent, resetRunActivity } from '../lib/agentStream'
import {
  buildPlanRevisionRequest,
  formatPlanRevisionMarkdown,
  parsePlanRevisionRequest
} from '../lib/planFeedback'
import {
  buildStoryEditRequest,
  formatStoryEditMarkdown,
  parseStoryEditRequest
} from '../lib/storyEditFeedback'
import PlanModal from './PlanModal'
import StoryEditModal from './StoryEditModal'
import PillButton from './PillButton'
import type { ReviewComment } from './ReviewComments'
import DebugModal from './DebugModal'

const THINKING_PHRASES = [
  'Thinking',
  'Rummaging through your story',
  'Pondering',
  'Weaving ideas',
  'Consulting the muse',
  'Crafting a response',
  'Mulling it over',
  'Searching for the right words'
]

// Matches the server's express.json() body limit for conversation saves (see index.ts) — the
// conversation size meter shows how close a conversation's saved payload is to that ceiling.
const CONVERSATION_SIZE_LIMIT_BYTES = 10 * 1024 * 1024

// How often the cross-conversation run lock re-checks the server's run registry while it
// believes another conversation is writing. Exported so tests can assert on/override it
// without fighting fake timers.
export const RUNNING_ELSEWHERE_POLL_MS = 2500
// Retry interval after a failed activeRun check — only used while currently locked (see the
// effect below), so a network blip can't brick the composer but a real lock still clears.
export const RUNNING_ELSEWHERE_RETRY_MS = 5000

// Once a generation has been running this long, the rotating phrases (or whatever
// status the agent last reported) give way to an acknowledgment that it's taking
// a bit longer than usual.
const LONG_WAIT_THRESHOLD_MS = 12000
const LONG_WAIT_PHRASES = [
  'Good scenes take a little longer to write',
  'Still with you — just working through the details',
  'The wait will be worth it',
  'Almost there',
  'Somewhere, a muse is being very stubborn',
  'Negotiating with the plot',
  'This scene is playing hard to get',
  'Definitely not lost, just taking the scenic route',
  "Counting plot holes so you don't have to",
  'Waiting on the muse to finish its coffee',
  'The characters are refusing to cooperate',
  'Rewriting this sentence for the fourth time',
  'Consulting a very indecisive narrator',
  'Herding a few stray subplots',
  'The villain wants a rewrite of their motivation',
  "Still faster than writer's block"
]

function ThinkingIndicator({ label }: { label?: string }) {
  const [phraseIdx, setPhraseIdx] = useState(() =>
    Math.floor(Math.random() * THINKING_PHRASES.length)
  )
  const [longWaitPhraseIdx, setLongWaitPhraseIdx] = useState(() =>
    Math.floor(Math.random() * LONG_WAIT_PHRASES.length)
  )
  const [isLongWait, setIsLongWait] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIdx((i) => (i + 1) % THINKING_PHRASES.length)
    }, 2200)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // These stick around 5x longer than the regular thinking phrases — they're
    // meant to be read and smiled at, not skimmed past.
    const interval = setInterval(() => {
      setLongWaitPhraseIdx((i) => (i + 1) % LONG_WAIT_PHRASES.length)
    }, 2200 * 5)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => setIsLongWait(true), LONG_WAIT_THRESHOLD_MS)
    return () => clearTimeout(timeout)
  }, [])

  const displayText = isLongWait
    ? LONG_WAIT_PHRASES[longWaitPhraseIdx]
    : label || THINKING_PHRASES[phraseIdx]

  return (
    <div className="thinking-indicator">
      <span className="thinking-text">{displayText}</span>
      <span className="thinking-dots">
        <span />
        <span />
        <span />
      </span>
    </div>
  )
}

// A single tool-call/reasoning entry: a heading row plus its detail directly beneath it.
// Expanded by default so the detail is visible immediately; click the row to collapse it.
function AgentActivityLogItem({ item }: { item: AgentActivityItem }): React.JSX.Element {
  const [expanded, setExpanded] = useState(true)
  // The intent preamble is a single tweet-sized sentence shown before the agent starts taking
  // action — there's nothing to collapse, so it renders as a plain line, not a toggleable row.
  if (item.kind === 'intent') {
    return (
      <p className="agent-activity-log-item-intent">
        <Sparkles size={12} />
        {item.detail}
      </p>
    )
  }
  return (
    <div className="agent-activity-log-item">
      <button
        type="button"
        className="agent-activity-log-item-row"
        onClick={() => setExpanded((value) => !value)}
      >
        <span className="agent-activity-log-item-row-label">
          {item.kind === 'reasoning' ? <Brain size={12} /> : <Wrench size={12} />}
          {item.label}
        </span>
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {expanded && <p className="agent-activity-log-item-detail">{item.detail}</p>}
    </div>
  )
}

// Shows tool calls and reasoning ("thinking mode") the agent produced this turn — each entry is
// its own row with its own heading/chevron and expands independently (AgentActivityLogItem);
// no outer group toggle or bordered box, just a plain stack of rows in the flow of the message.
function AgentActivityLog({
  items,
  planSummary
}: {
  items: AgentActivityItem[]
  planSummary?: string
}): React.JSX.Element {
  let hasPlanActivity = false
  const visibleItems = items.flatMap((item) => {
    if (item.label !== 'Proposed a plan') return [item]
    if (hasPlanActivity) return []
    hasPlanActivity = true
    return [
      {
        ...item,
        detail: item.detail.trim() || planSummary?.trim() || 'Prepared the outline for review.'
      }
    ]
  })

  return (
    <div className="agent-activity-log">
      {visibleItems.map((item, index) => (
        <AgentActivityLogItem key={index} item={item} />
      ))}
    </div>
  )
}

// A small ring meter (admin-only) showing how close this conversation's saved payload is to the
// server's body-size limit — same idea as Claude Code's own context-usage indicator: a filled
// ring plus the percentage, colored green/yellow/orange/red as it climbs, so a conversation
// trending toward a silently-failed save is visible before it happens.
function ConversationSizeMeter({
  percent,
  formatted
}: {
  percent: number
  formatted: string
}): React.JSX.Element {
  const radius = 7
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - percent / 100)
  const severityClass =
    percent >= 90
      ? 'conversation-size-meter-red'
      : percent >= 70
        ? 'conversation-size-meter-orange'
        : percent >= 40
          ? 'conversation-size-meter-yellow'
          : 'conversation-size-meter-green'

  return (
    <span
      className={`conversation-size-meter ${severityClass}`}
      title={`Conversation save payload: ${formatted} (${percent}% of the 10MB limit)`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" className="conversation-size-meter-ring">
        <circle
          cx="8"
          cy="8"
          r={radius}
          className="conversation-size-meter-ring-track"
          fill="none"
          strokeWidth="2"
        />
        <circle
          cx="8"
          cy="8"
          r={radius}
          className="conversation-size-meter-ring-fill"
          fill="none"
          strokeWidth="2"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 8 8)"
        />
      </svg>
      <span className="conversation-size-meter-percent">{percent}%</span>
    </span>
  )
}

export interface PlanComment {
  id: string
  lineIndex: number
  lineText: string
  comment: string
  resolved?: boolean
}

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  metadata?: {
    clarifyActions?: AgentAction[]
    selection?: { text: string; instructions: string }
    guideItemIds?: string[]
    mode?: 'plan' | 'ask' | 'writing' | 'edit'
    isPlanRevision?: boolean
    planToExecute?: string
    plan?: { summary: string; content: string; comments?: PlanComment[]; isRevision?: boolean }
    // An edit request built from the writer's comments on a story block (see StoryEditModal).
    isStoryEdit?: boolean
    storyEditSource?: { messageId: string; blockIndex: number; content: string }
    // Per story-block review state on an assistant message, keyed by block index.
    storyEdits?: Record<number, { comments?: ReviewComment[]; previousContent?: string }>
    debug?: AgentDebugEntry[]
    activity?: AgentActivityItem[]
    timing?: AgentTiming
  }
}

function formatElapsed(elapsedMs: number): string {
  if (elapsedMs < 1000) return '<1s'
  const totalSeconds = Math.floor(elapsedMs / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`
}

function AgentWorkDisclosure({
  items,
  timing,
  isActive,
  liveElapsedMs,
  status,
  planSummary
}: {
  items?: AgentActivityItem[]
  timing?: AgentTiming
  isActive: boolean
  liveElapsedMs: number
  status?: string | null
  planSummary?: string
}): React.JSX.Element | null {
  const hasDetails = isActive || Boolean(items?.length)
  const [expanded, setExpanded] = useState(isActive)

  // Preserve the old activity-only presentation for messages saved before timing shipped.
  if (!isActive && !timing) {
    return items?.length ? <AgentActivityLog items={items} planSummary={planSummary} /> : null
  }

  const label = isActive
    ? liveElapsedMs < 1000
      ? 'Working…'
      : `Working for ${formatElapsed(liveElapsedMs)}`
    : `Worked for ${formatElapsed(timing?.elapsedMs || 0)}`

  return (
    <section className="agent-work-disclosure">
      {hasDetails ? (
        <button
          type="button"
          className="agent-work-disclosure-trigger"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <span>{label}</span>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : (
        <p className="agent-work-disclosure-label">{label}</p>
      )}
      {expanded && hasDetails && (
        <div className="agent-work-disclosure-content">
          {items && items.length > 0 && (
            <AgentActivityLog items={items} planSummary={planSummary} />
          )}
          {isActive && <ThinkingIndicator label={status || undefined} />}
        </div>
      )}
    </section>
  )
}

function guideItemIdsFromText(text: string): string[] {
  return Array.from(
    new Set(
      [...text.matchAll(/\[\[wiki:[^:\]]+:([^|\]]+)(?:\|[^\]]+)?\]\]/g)].map((match) => match[1])
    )
  )
}

function getSelectionDisplay(message: Message): { text: string; instructions: string } | null {
  if (message.metadata?.selection) return message.metadata.selection
  const match = message.content.match(
    /<SELECTED_TEXT>\s*([\s\S]*?)\s*<\/SELECTED_TEXT>\s*\n+\s*My instructions:\s*\n([\s\S]*?)(?:\n\s*Return each replacement option as a separate \[STORY\][\s\S]*)?$/i
  )
  if (!match) return null
  return { text: match[1].trim(), instructions: match[2].trim() }
}

function getPlanToExecute(message: Message): string | null {
  if (message.metadata?.planToExecute) return message.metadata.planToExecute
  const match = message.content.match(/<APPROVED_PLAN>\s*([\s\S]*?)\s*<\/APPROVED_PLAN>/i)
  return match?.[1]?.trim() || null
}

interface ConversationViewProps {
  conversation: {
    id: string
    name: string
    mode: 'plan' | 'ask' | 'writing'
    messages: Message[]
  }
  storyId: string
  wiki: WikiPage[]
  storyContent: string
  settings: AppSettings
  onSaveSettings: (settings: AppSettings) => void
  onUpdateMessages: (conversationId: string, messages: Message[]) => void
  onInsertIntoStory: (text: string, mode?: 'cursor' | 'append' | 'replace') => void
  onClose?: () => void
  onNewChat?: () => void
  onShowHistory?: () => void
  onOpenConversation?: (conversationId: string) => void
  onRename?: () => void
  hasSelection: boolean
  onGenerationComplete?: (text: string) => void
  onNavigateToWiki?: (type: string, id: string) => void
  onWikiUpdated?: (items: WikiPage[]) => void
  autoSendMessageId?: string
  onAutoSendHandled?: () => void
  onSetConversationMode: (conversationId: string, mode: 'plan' | 'ask' | 'writing') => void
  onConversationTitleGenerated?: (conversationId: string, title: string) => void
}

export default function ConversationView({
  conversation,
  storyId,
  wiki,
  storyContent,
  settings,
  onSaveSettings,
  onUpdateMessages,
  onInsertIntoStory,
  onClose,
  onNewChat,
  onShowHistory,
  onOpenConversation,
  onRename,
  hasSelection,
  onGenerationComplete,
  onNavigateToWiki,
  onWikiUpdated,
  autoSendMessageId,
  onAutoSendHandled,
  onSetConversationMode,
  onConversationTitleGenerated
}: ConversationViewProps): React.JSX.Element {
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  // Which conversation's run `loading` belongs to. `loading` itself is instance-wide (this
  // component stays mounted across conversation switches), so this is what lets the composer
  // tell "my run is in flight" apart from "some other conversation's run is in flight".
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastAttemptMessages, setLastAttemptMessages] = useState<Message[] | null>(null)
  const [activeAssistantMessageId, setActiveAssistantMessageId] = useState<string | null>(null)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [agentStatus, setAgentStatus] = useState<string | null>(null)
  const [planModalMessageId, setPlanModalMessageId] = useState<string | null>(null)
  const [storyEditTarget, setStoryEditTarget] = useState<{
    messageId: string
    blockIndex: number
  } | null>(null)
  const [debugModalMessageId, setDebugModalMessageId] = useState<string | null>(null)
  const [liveElapsedMs, setLiveElapsedMs] = useState(0)
  // True while `loading` belongs to a run this client reattached to rather than started. Such
  // a run has no requestId here (it died with the page that sent it), so there is nothing to
  // cancel and the Stop button would be inert — it stays hidden instead.
  const [isReattachedRun, setIsReattachedRun] = useState(false)
  // Bumped when a live send fails, to re-run the reattach effect: the server run may well have
  // survived the broken stream, and the reply it persists lands on the placeholder we kept.
  const [reattachNonce, setReattachNonce] = useState(0)
  // Conversation id of a run in flight elsewhere for this story, per the server's run registry —
  // not this component's own `loading`/`loadingConversationId` state, which only knows about a
  // run this mounted instance is itself tracking (live-sent or reattached). This is what closes
  // the holes that state alone leaves: a page refresh mid-run (no mounted instance has state to
  // survive it) and opening a different conversation via chat history (a fresh mount, no prior
  // state at all). See the effect below.
  const [runningElsewhereId, setRunningElsewhereId] = useState<string | null>(null)
  const guideMentionTrigger = useMemo<ChatComposerTrigger>(() => {
    const guideItems: SearchableItem<{ type: string }>[] = wiki
      .filter((page) => page.parentId === null)
      .map((page) => ({
        id: page.id,
        label: page.name,
        auxiliaryData: { type: page.type }
      }))
    return {
      character: '@',
      searchSource: createStaticSource(guideItems),
      renderItem: (item) => (
        <TypeaheadItem
          item={item}
          description={String((item.auxiliaryData as { type?: string })?.type || '')}
        />
      ),
      onSelect: (item) => ({
        value: `[[wiki:${String((item.auxiliaryData as { type?: string })?.type || 'note')}:${item.id}|${item.label}]]`,
        label: item.label,
        variant: 'blue' as const
      })
    }
  }, [wiki])

  const resolveWikiLinks = (text: string): string => {
    if (!wiki || wiki.length === 0) return text
    return text.replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, target, label) => {
      const trimmedTarget = target.trim()
      const displayText = label ? label.trim() : trimmedTarget

      if (trimmedTarget.startsWith('wiki:')) {
        const parts = trimmedTarget.split(':')
        const id = parts[2]
        const found = wiki.find((item: WikiPage) => item.id === id)
        if (found) {
          return `[${displayText}](wiki://${found.type}/${found.id})`
        }
        return `<span class="wiki-link-broken">${displayText}</span>`
      }

      const found = wiki.find(
        (item: WikiPage) => item.name.trim().toLowerCase() === trimmedTarget.toLowerCase()
      )
      if (found) {
        return `[${displayText}](wiki://${found.type}/${found.id})`
      }
      return `<span class="wiki-link-broken">${displayText}</span>`
    })
  }

  const handleChatClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const anchor = target.closest('a')
    if (anchor) {
      const href = anchor.getAttribute('href')
      if (href && href.startsWith('wiki://')) {
        e.preventDefault()
        const parts = href.replace('wiki://', '').split('/')
        const type = parts[0]
        const id = parts[1]
        if (onNavigateToWiki) {
          onNavigateToWiki(type, id)
        }
      }
    }
  }

  useEffect(() => {
    setError(null)
    setLastAttemptMessages(null)
  }, [conversation.id])

  useEffect(() => {
    const handleGlobalClick = () => {
      setOpenDropdownId(null)
    }
    document.addEventListener('click', handleGlobalClick)
    return () => document.removeEventListener('click', handleGlobalClick)
  }, [])

  const chatEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)
  const lastScrolledConversationIdRef = useRef<string | null>(null)
  const completionInFlightRef = useRef(false)
  const activeRequestIdRef = useRef<string | null>(null)
  const activeStreamMessagesRef = useRef<Message[]>([])
  const interruptedRequestIdsRef = useRef(new Set<string>())
  const handledAutoSendMessageIdRef = useRef<string | null>(null)
  const lastIssuedTimestampRef = useRef(0)
  const liveTimerStartedAtRef = useRef<number | null>(null)
  // The reattach effect's poll loop runs across many renders and cannot read `loading` from
  // the closure it was created in — that value is frozen at effect-creation time, so a poll
  // that deferred on a stale `true` would defer forever. It reads this instead.
  const loadingRef = useRef(false)
  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    if (!loading || !activeAssistantMessageId || liveTimerStartedAtRef.current === null) return
    const interval = setInterval(() => {
      setLiveElapsedMs(Math.max(0, performance.now() - (liveTimerStartedAtRef.current || 0)))
    }, 1000)
    return () => clearInterval(interval)
  }, [activeAssistantMessageId, loading])

  // A run started before this component mounted — the writer navigated away and came back.
  // Seed from the snapshot, then follow it. The reducer here is the same one the live stream
  // uses, so a caught-up run renders identically to one watched from the start.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let offset = 0
    let revision = 0
    let appliedMetadataCount = 0
    // The first snapshot is authoritative, not incremental. The seed below already contains
    // whatever the autosave persisted of this run — a partial draft, or the whole finished
    // reply — and the server's `since=0` delta is the same text from the top. Appending it
    // would render the reply twice and the autosave would then persist the duplicate.
    let isFirstDelta = true
    // Mirrors isFirstDelta for metadata: the target message may already carry activity entries
    // from a prior live stream or an autosaved snapshot, so the first metadata replay of this
    // effect instance must clear them before replaying, or they'd be duplicated on top.
    let hasResetActivity = false
    // Set once this effect has adopted the run and owns `loading`, so the deferral below
    // (which exists for a live send *elsewhere*) stops applying to our own polling.
    let ownsLoading = false
    // Owned entirely by this effect instance, seeded once. Every tick applies its delta on top
    // of this instead of re-reading `conversation.messages` (which is stale after the first
    // render this effect saw) — otherwise tick N would rebuild from that stale base plus only
    // the latest delta and the writer would watch the prose shrink back down each second.
    let workingMessages = conversation.messages

    const poll = async (): Promise<void> => {
      if (cancelled) return
      if (!ownsLoading && loadingRef.current) {
        // `loading` is instance-wide, not per-conversation — a live send elsewhere in this same
        // mount would otherwise permanently skip reattach for the conversation being switched
        // into. Wait it out instead of giving up. Read through the ref, never the closure: the
        // closure's value is frozen at effect creation and would strand this loop.
        timer = setTimeout(() => void poll(), 1500)
        return
      }
      let snapshot: AgentRunSnapshot
      try {
        snapshot = await window.api.agent.runStatus(storyId, conversation.id, offset, revision)
      } catch {
        // A failed poll is not a failed run; the next tick tries again.
        timer = setTimeout(() => void poll(), 1000)
        return
      }
      if (cancelled) return

      const assistantMessageId = snapshot.assistantMessageId
      if (snapshot.status === 'none') {
        const last = workingMessages[workingMessages.length - 1]
        if (last?.role === 'assistant' && !last.content.trim()) {
          setError("That response didn't finish. Try asking again.")
        }
        return
      }
      if (!assistantMessageId) return

      if (!hasResetActivity && (snapshot.metadataEvents || []).length > 0) {
        workingMessages = resetRunActivity(workingMessages, assistantMessageId)
        hasResetActivity = true
      }

      if (snapshot.textDelta) {
        const replaces = isFirstDelta || snapshot.replaceAll
        workingMessages = applyAgentEvent(
          workingMessages,
          { type: replaces ? 'replace' : 'token', text: snapshot.textDelta },
          { assistantMessageId, isPlanRevision: false }
        )
        offset = replaces ? snapshot.textDelta.length : offset + snapshot.textDelta.length
        isFirstDelta = false
      }
      // Unconditional: a metadata-only tick still needs to advance revision, or the next poll
      // re-requests the same revision and re-applies events we've already applied below.
      revision = snapshot.revision ?? revision
      // The server always returns the full metadata list for the run, not just what's new since
      // `since`/`rev` — replay only the tail we haven't applied yet, or every tick would
      // duplicate every earlier tick's activity entries.
      const metadataEvents = snapshot.metadataEvents || []
      for (const event of metadataEvents.slice(appliedMetadataCount)) {
        workingMessages = applyAgentEvent(workingMessages, event, {
          assistantMessageId,
          isPlanRevision: false
        })
      }
      appliedMetadataCount = metadataEvents.length
      onUpdateMessages(conversation.id, workingMessages)

      if (snapshot.status === 'running') {
        ownsLoading = true
        loadingRef.current = true
        setLoading(true)
        setLoadingConversationId(conversation.id)
        setIsReattachedRun(true)
        setActiveAssistantMessageId(assistantMessageId)
        setAgentStatus(snapshot.statusLabel || null)
        timer = setTimeout(() => void poll(), 1000)
        return
      }
      // Both resolutions land here — 'done' and 'error'. Releasing ownership matters as much
      // as clearing the flag: this closure outlives the run, and a later live send in this
      // same conversation does not re-run the effect (its deps did not change). A stale
      // `ownsLoading` would make the cleanup below clear that send's `loading` when the writer
      // switches conversation — the composer re-enables, and the next message typed into it is
      // swallowed by completionInFlightRef with no sign anything went wrong.
      if (ownsLoading) {
        ownsLoading = false
        loadingRef.current = false
        setLoading(false)
        setLoadingConversationId(null)
      }
      setIsReattachedRun(false)
      setAgentStatus(null)
      setActiveAssistantMessageId(null)
      if (snapshot.status === 'error') setError(snapshot.error || 'The agent failed.')
    }

    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      if (ownsLoading) {
        // This instance owned `loading`; leaving it set would freeze the composer of whatever
        // conversation the writer switched to.
        ownsLoading = false
        loadingRef.current = false
        setLoading(false)
        setLoadingConversationId(null)
        setIsReattachedRun(false)
      }
    }
    // Re-runs when the writer switches conversation, which is exactly when a different run
    // might be in flight, and when a live send fails (reattachNonce) so the surviving server
    // run can be followed to its reply. `loading` is intentionally excluded — poll() reads it
    // through loadingRef instead, so a live send elsewhere doesn't tear this down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id, storyId, reattachNonce])

  // Cross-conversation companion to the reattach effect above. That effect only ever finds a
  // run for *this* conversation; it has nothing to say about a run in flight for some other
  // conversation of this story, which is exactly the case a page refresh or a fresh mount (via
  // chat history) loses all local state for. The server's run registry is the one thing that
  // survives both, so ask it directly: window.api.agent.activeRun reports the conversation (if
  // any) that currently owns a running turn for this story.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    // Owned by this effect instance's own poll loop, not React state — mirrors the reattach
    // effect's `ownsLoading` pattern so the fail-open retry below reads the loop's own idea of
    // "currently locked" rather than a state value that may not have committed yet.
    let locked = false

    const check = async (): Promise<void> => {
      if (cancelled) return
      let result: { conversationId: string | null }
      try {
        result = await window.api.agent.activeRun(storyId)
      } catch {
        // Fail open: a network blip must never brick the composer. Only keep retrying while
        // we're currently locked, so a real unlock still eventually arrives; an unlocked
        // composer just stays unlocked until the next mount/switch tries again.
        if (!cancelled && locked) {
          timer = setTimeout(() => void check(), RUNNING_ELSEWHERE_RETRY_MS)
        }
        return
      }
      if (cancelled) return
      if (result.conversationId && result.conversationId !== conversation.id) {
        locked = true
        setRunningElsewhereId(result.conversationId)
        timer = setTimeout(() => void check(), RUNNING_ELSEWHERE_POLL_MS)
      } else {
        // Either nothing is running, or it's this conversation's own run — the reattach effect
        // above already surfaces that case (Stop/thinking indicator), so no lock is needed here.
        locked = false
        setRunningElsewhereId(null)
      }
    }

    void check()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [conversation.id, storyId])

  // Guarantees strictly increasing timestamps even when several messages are created
  // in the same tick (e.g. system + user + assistant placeholder). Ties here get
  // broken arbitrarily by Postgres on refetch, which reorders messages on revisit.
  const nextTimestamp = useCallback((): number => {
    const ts = Math.max(Date.now(), lastIssuedTimestampRef.current + 1)
    lastIssuedTimestampRef.current = ts
    return ts
  }, [])

  const scrollToBottom = (behavior: ScrollBehavior = 'auto'): void => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior
      })
    }
  }

  const handleScroll = (): void => {
    if (!scrollContainerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // User is near bottom if they are within 120px of the bottom edge
    const atBottom = scrollHeight - scrollTop - clientHeight < 120
    isNearBottomRef.current = atBottom
  }

  useLayoutEffect(() => {
    const isOpeningConversation = lastScrolledConversationIdRef.current !== conversation.id
    lastScrolledConversationIdRef.current = conversation.id

    const messages = conversation.messages
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1]
      // Always auto-scroll back down on a new user query
      if (lastMsg.role === 'user') {
        isNearBottomRef.current = true
      }
    }

    let timer: NodeJS.Timeout | undefined
    if (isNearBottomRef.current || loading) {
      const behavior = isOpeningConversation || loading ? 'auto' : 'smooth'
      // Scroll instantly to stay in sync
      scrollToBottom(behavior)
      // Defer a microtask scroll to capture layout finishes
      timer = setTimeout(() => {
        scrollToBottom(behavior)
      }, 0)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [conversation.id, conversation.messages, loading])

  const startAICompletion = useCallback(
    async (baseMessages: Message[]): Promise<void> => {
      if (completionInFlightRef.current) return
      completionInFlightRef.current = true
      // The system prompt is added lazily, right as the writer's first
      // message in this conversation is sent, rather than at conversation
      // creation time.
      const hasSystemMessage = baseMessages.some((message) => message.role === 'system')
      const messagesWithSystemPrompt = hasSystemMessage
        ? baseMessages
        : [
            {
              id: createId(),
              role: 'system' as const,
              content: settings.systemPromptTemplate || 'You are an expert story planner.',
              timestamp: nextTimestamp()
            },
            ...baseMessages
          ]
      const sanitizedBaseMessages = messagesWithSystemPrompt.filter(
        (message) => message.role !== 'assistant' || message.content.trim().length > 0
      )
      setError(null)
      setLoading(true)
      loadingRef.current = true
      setLoadingConversationId(conversation.id)
      // This turn is ours, with a live requestId behind it — Stop applies again.
      setIsReattachedRun(false)
      setLastAttemptMessages(sanitizedBaseMessages)

      let activeModel = settings.selectedModel

      if (!activeModel) {
        try {
          const models = await listModels()
          if (models && models.length > 0) {
            activeModel = models[0].id
            onSaveSettings({
              ...settings,
              selectedModel: activeModel
            })
          } else {
            throw new Error('No models are available. Add a provider key to the server .env.')
          }
        } catch (err: any) {
          console.error('Failed to auto-initialize model:', err)
          setError(
            err.message || 'Failed to auto-detect LLM model. Ensure your LLM server is running.'
          )
          setLoading(false)
          loadingRef.current = false
          setLoadingConversationId(null)
          completionInFlightRef.current = false
          return
        }
      }

      // Immediately show user message and an empty placeholder assistant message
      const assistantMessageId = createId()
      const initialAssistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: nextTimestamp()
      }

      setActiveAssistantMessageId(assistantMessageId)
      let currentMessages = [...sanitizedBaseMessages, initialAssistantMessage]
      activeStreamMessagesRef.current = currentMessages
      onUpdateMessages(conversation.id, currentMessages)

      const reqId = createId()
      activeRequestIdRef.current = reqId
      liveTimerStartedAtRef.current = performance.now()
      setLiveElapsedMs(0)
      let responseCompleted = false

      try {
        const sourceUserMessage = [...sanitizedBaseMessages]
          .reverse()
          .find((message) => message.role === 'user')
        const sourceMessageId = sourceUserMessage?.id
        const guideItemIds = sourceUserMessage?.metadata?.guideItemIds || []
        const mode = sourceUserMessage?.metadata?.mode || conversation.mode
        // An edit request is deliberately context-free: the edit agent gets the passage and the
        // comments in that one message and nothing else, so it cannot rewrite from anything the
        // writer did not point at.
        const outgoingMessages =
          mode === 'edit' && sourceUserMessage ? [sourceUserMessage] : sanitizedBaseMessages
        const storyEditSource = sourceUserMessage?.metadata?.storyEditSource
        const assistantReply = await window.api.agent.run(
          storyId,
          conversation.id,
          {
            messages: outgoingMessages.map(({ role, content }) => ({ role, content })),
            storyContent,
            requestId: reqId,
            assistantMessageId,
            sourceMessageId,
            guideItemIds,
            mode
          },
          (event) => {
            if (interruptedRequestIdsRef.current.has(reqId)) return
            if (event.type === 'status') {
              if (!responseCompleted && activeRequestIdRef.current === reqId) {
                setAgentStatus(event.label)
              }
            } else if (event.type === 'wiki_updated') {
              onWikiUpdated?.(event.items)
            } else if (
              event.type === 'token' ||
              event.type === 'replace' ||
              event.type === 'clarify_actions' ||
              event.type === 'plan' ||
              event.type === 'debug' ||
              event.type === 'activity'
            ) {
              currentMessages = applyAgentEvent(currentMessages, event, {
                assistantMessageId,
                isPlanRevision: Boolean(sourceUserMessage?.metadata?.isPlanRevision)
              })
              activeStreamMessagesRef.current = currentMessages
              onUpdateMessages(conversation.id, currentMessages)
            } else if (event.type === 'conversation_title') {
              onConversationTitleGenerated?.(conversation.id, event.title)
            } else if (event.type === 'done') {
              responseCompleted = true
              currentMessages = currentMessages.map((message) =>
                message.id === assistantMessageId
                  ? {
                      ...message,
                      content: event.text,
                      metadata: {
                        ...message.metadata,
                        ...(event.timing ? { timing: event.timing } : {}),
                        // The reply to an edit request revises the block it was built from;
                        // remembering that block is what lets its modal show the diff.
                        ...(storyEditSource
                          ? { storyEdits: { 0: { previousContent: storyEditSource.content } } }
                          : {})
                      }
                    }
                  : message
              )
              activeStreamMessagesRef.current = currentMessages
              onUpdateMessages(conversation.id, currentMessages)
              onGenerationComplete?.(event.text)
              posthog.capture('ai_generation_completed', {
                response_length: event.text.length,
                message_count: sanitizedBaseMessages.length
              })
              setError(null)
              setLastAttemptMessages(null)
              completionInFlightRef.current = false
              if (activeRequestIdRef.current === reqId) {
                setLoading(false)
                loadingRef.current = false
                setLoadingConversationId(null)
                liveTimerStartedAtRef.current = null
                setAgentStatus(null)
                setActiveAssistantMessageId(null)
                activeRequestIdRef.current = null
              }
            }
          }
        )

        if (interruptedRequestIdsRef.current.has(reqId)) return
        if (!responseCompleted) {
          onUpdateMessages(
            conversation.id,
            currentMessages.map((m) =>
              m.id === assistantMessageId ? { ...m, content: assistantReply } : m
            )
          )
          onGenerationComplete?.(assistantReply)
          posthog.capture('ai_generation_completed', {
            response_length: assistantReply.length,
            message_count: sanitizedBaseMessages.length
          })
          setError(null)
          setLastAttemptMessages(null)
        }
      } catch (err: any) {
        console.error(err)
        posthog.captureException(err, { flow: 'ai_generation' })
        posthog.capture('ai_generation_failed', {
          was_cancelled: err.message === 'Request cancelled.'
        })
        const wasInterrupted =
          interruptedRequestIdsRef.current.has(reqId) || err.message === 'Request cancelled.'
        if (wasInterrupted) {
          setError(null)
        } else {
          setError(err.message || 'Failed to get completion from local LLM.')
        }

        // The placeholder stays, even when empty. A broken stream is not a dead run: the
        // server keeps generating and will persist its reply under this very id, and dropping
        // the bubble here would have the autosave's full-replace DELETE take that row out from
        // under it. An empty assistant message with no run behind it already renders as a
        // failed turn (see the reattach effect's 'none' branch), so nothing is lost visually.
        if (!wasInterrupted) {
          // Follow whatever the server is still doing with this turn. A cancelled one is
          // excluded on purpose — the writer withdrew it, so there is nothing to catch up on.
          setReattachNonce((nonce) => nonce + 1)
        }
      } finally {
        interruptedRequestIdsRef.current.delete(reqId)
        if (!responseCompleted) completionInFlightRef.current = false
        if (activeRequestIdRef.current === reqId) {
          setLoading(false)
          loadingRef.current = false
          setLoadingConversationId(null)
          liveTimerStartedAtRef.current = null
          setAgentStatus(null)
          setActiveAssistantMessageId(null)
          activeRequestIdRef.current = null
        }
      }
    },
    [
      settings,
      onSaveSettings,
      storyContent,
      storyId,
      conversation.id,
      onUpdateMessages,
      onWikiUpdated,
      onGenerationComplete,
      onConversationTitleGenerated
    ]
  )

  useEffect(() => {
    if (!autoSendMessageId || handledAutoSendMessageIdRef.current === autoSendMessageId) return
    const targetMessage = conversation.messages.find(
      (message) => message.id === autoSendMessageId && message.role === 'user'
    )
    if (!targetMessage) return

    handledAutoSendMessageIdRef.current = autoSendMessageId
    onAutoSendHandled?.()
    void startAICompletion(conversation.messages)
  }, [autoSendMessageId, conversation.messages, onAutoSendHandled, startAICompletion])

  // `loading` is instance-wide (this component stays mounted across conversation switches), so
  // it alone can't tell this conversation's run apart from one running elsewhere in the same
  // instance. Gate every presentational "is this conversation working" check on this instead.
  const isThisConversationLoading = loading && loadingConversationId === conversation.id

  // True when the composer must not be usable here even though nothing in *this* instance's own
  // state says it's busy: either another mounted conversation in this instance owns `loading`,
  // or the server's run registry says a different conversation of this story is writing
  // (runningElsewhereId — covers a page refresh or a fresh mount via chat history, where no
  // local state survived to say so). This makes "one run per story at a time" the composer's
  // rule even across refreshes, deliberately more conservative than the server, which only
  // enforces one run per *conversation* — matching the single-slot `loading`/stream state this
  // component already keeps.
  const lockedByOther = Boolean(runningElsewhereId) || (loading && !isThisConversationLoading)
  // The conversation holding the lock: a background run found by polling, or the live run
  // this same mount is streaming for another conversation.
  const busyConversationId = runningElsewhereId ?? loadingConversationId

  const handleSend = async (): Promise<void> => {
    if (!input.trim() || loading || lockedByOther) return

    const userMessage: Message = {
      id: createId(),
      role: 'user',
      content: input,
      timestamp: nextTimestamp(),
      metadata: { guideItemIds: guideItemIdsFromText(input), mode: conversation.mode }
    }

    const updatedMessages = [...conversation.messages, userMessage]
    setInput('')
    await startAICompletion(updatedMessages)
  }

  const handleReask = async (
    content: string,
    modeOverride?: 'plan' | 'ask' | 'writing' | 'edit',
    baseMessages?: Message[],
    isPlanRevision?: boolean,
    planToExecute?: string,
    selection?: { text: string; instructions: string },
    storyEditSource?: { messageId: string; blockIndex: number; content: string }
  ): Promise<void> => {
    if (loading || lockedByOther) return

    const userMessage: Message = {
      id: createId(),
      role: 'user',
      content: content,
      timestamp: nextTimestamp(),
      metadata: {
        guideItemIds: guideItemIdsFromText(content),
        mode: modeOverride ?? conversation.mode,
        isPlanRevision,
        planToExecute,
        // Kept alongside the plan so the message renders its passage card rather than the raw
        // <SELECTED_TEXT> boilerplate if it is ever shown without the plan.
        ...(selection ? { selection } : {}),
        ...(storyEditSource ? { isStoryEdit: true, storyEditSource } : {})
      }
    }

    const updatedMessages = [...(baseMessages ?? conversation.messages), userMessage]
    await startAICompletion(updatedMessages)
  }

  const handleSetPlanComments = (messageId: string, comments: PlanComment[]): void => {
    const updated = conversation.messages.map((message) =>
      message.id === messageId && message.metadata?.plan
        ? {
            ...message,
            metadata: { ...message.metadata, plan: { ...message.metadata.plan, comments } }
          }
        : message
    )
    onUpdateMessages(conversation.id, updated)
  }

  const setStoryEditState = (
    messages: Message[],
    messageId: string,
    blockIndex: number,
    change: (current: { comments?: ReviewComment[]; previousContent?: string }) => {
      comments?: ReviewComment[]
      previousContent?: string
    }
  ): Message[] =>
    messages.map((message) => {
      if (message.id !== messageId) return message
      const storyEdits = message.metadata?.storyEdits || {}
      return {
        ...message,
        metadata: {
          ...message.metadata,
          storyEdits: { ...storyEdits, [blockIndex]: change(storyEdits[blockIndex] || {}) }
        }
      }
    })

  const handleSetStoryEditComments = (
    messageId: string,
    blockIndex: number,
    comments: ReviewComment[]
  ): void => {
    onUpdateMessages(
      conversation.id,
      setStoryEditState(conversation.messages, messageId, blockIndex, (current) => ({
        ...current,
        comments
      }))
    )
  }

  const handleRequestStoryEdits = (
    messageId: string,
    blockIndex: number,
    content: string,
    comments: ReviewComment[]
  ): void => {
    const unresolved = comments.filter((comment) => !comment.resolved)
    if (unresolved.length === 0) return
    const request = buildStoryEditRequest(content, unresolved)
    // The comments stay on the block, resolved, so reopening it later shows what was asked.
    const resolvedMessages = setStoryEditState(
      conversation.messages,
      messageId,
      blockIndex,
      (current) => ({
        ...current,
        comments: comments.map((comment) => ({ ...comment, resolved: true }))
      })
    )
    setStoryEditTarget(null)
    void handleReask(request, 'edit', resolvedMessages, undefined, undefined, undefined, {
      messageId,
      blockIndex,
      content
    })
  }

  const handleRequestPlanChanges = (
    messageId: string,
    planContent: string,
    comments: PlanComment[]
  ): void => {
    const unresolvedComments = comments.filter((comment) => !comment.resolved)
    if (unresolvedComments.length === 0 || loading) return
    const revisionRequest = buildPlanRevisionRequest(planContent, unresolvedComments)
    // Mark comments resolved rather than clearing them, so they stay visible on this
    // plan version if the writer reopens it later to see what they'd asked for.
    const resolvedMessages = conversation.messages.map((message) =>
      message.id === messageId && message.metadata?.plan
        ? {
            ...message,
            metadata: {
              ...message.metadata,
              plan: {
                ...message.metadata.plan,
                comments: comments.map((comment) => ({ ...comment, resolved: true }))
              }
            }
          }
        : message
    )
    setPlanModalMessageId(null)
    void handleReask(revisionRequest, 'plan', resolvedMessages, true)
  }

  // The selection a chat was started from, if any — the first user message the selection bubble
  // built. Later turns (bare instructions, plan revisions) never repeat it.
  const originSelection = (): { text: string; instructions: string } | null => {
    for (const message of conversation.messages) {
      if (message.role !== 'user') continue
      const selection = getSelectionDisplay(message)
      if (selection) return selection
    }
    return null
  }

  const handleExecutePlan = (planContent: string): void => {
    if (loading) return
    onSetConversationMode(conversation.id, 'writing')
    setPlanModalMessageId(null)
    const selection = originSelection()
    void handleReask(
      buildExecutePlanPrompt(planContent, selection),
      'writing',
      undefined,
      false,
      planContent,
      selection ?? undefined
    )
  }

  const handleCancelRequest = async (): Promise<void> => {
    const requestId = activeRequestIdRef.current
    const assistantMessageId = activeAssistantMessageId
    if (!requestId) return
    interruptedRequestIdsRef.current.add(requestId)
    activeRequestIdRef.current = null
    completionInFlightRef.current = false
    const interruptedMessages = activeStreamMessagesRef.current.map((message) => {
      if (!assistantMessageId || message.id !== assistantMessageId) return message
      const partial = message.content.trimEnd()
      return {
        ...message,
        content: partial ? `${partial}\n\n*Agent interrupted.*` : '*Agent interrupted.*'
      }
    })
    activeStreamMessagesRef.current = interruptedMessages
    onUpdateMessages(conversation.id, interruptedMessages)
    setLoading(false)
    loadingRef.current = false
    setLoadingConversationId(null)
    liveTimerStartedAtRef.current = null
    setAgentStatus(null)
    setActiveAssistantMessageId(null)
    setError(null)
    try {
      await window.api.agent.cancel(requestId)
    } catch (err) {
      console.error('Failed to notify the server about agent cancellation:', err)
    }
  }

  const handleCopyText = (text: string): void => {
    // Both flavours: pasted into the manuscript this arrives as real paragraphs with the markdown
    // rendered, and pasted anywhere else it is still the plain markdown the writer copied.
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      const html = markdownToManuscriptHtml(text)
      void navigator.clipboard
        .write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
          })
        ])
        .catch(() => navigator.clipboard.writeText(text))
      return
    }
    void navigator.clipboard.writeText(text)
  }

  const quickPrompts = [
    'Brainstorm a plot twist based on active scenes',
    'Describe the atmosphere of the current setting',
    'Develop a conflict between two of my characters',
    "Outline the next chapter's scenes"
  ]

  const visibleMessages = conversation.messages.filter(
    (message) =>
      message.role !== 'system' &&
      (message.role !== 'assistant' ||
        message.content.trim().length > 0 ||
        (loading && message.id === activeAssistantMessageId))
  )
  const latestUserMessageIndex = visibleMessages.reduce(
    (latestIndex, message, index) => (message.role === 'user' ? index : latestIndex),
    -1
  )
  // A story block is superseded — viewable, no longer editable — once a later message carries
  // a story block. Blocks in the same message are alternatives ("give me two options"), so
  // they never supersede each other.
  // An assistant message does not record the mode it was produced in; the user message that
  // prompted it does (see handleSend/handleReask). Older conversations predate that metadata,
  // so a missing mode falls back to the mode-agnostic parse rather than the conversation's
  // current mode, which may have changed since.
  const modeForMessage = (messageIndex: number): NonNullable<Message['metadata']>['mode'] => {
    for (let index = messageIndex; index >= 0; index -= 1) {
      const message = visibleMessages[index]
      if (message.role === 'user') return message.metadata?.mode
    }
    return undefined
  }
  const blocksForMessage = (messageIndex: number) =>
    parseMessageBlocks(visibleMessages[messageIndex].content.replace(/^(\s*\.\.\.\s*)+/, ''), {
      mode: modeForMessage(messageIndex)
    })

  const hasLaterStoryBlock = (messageIndex: number): boolean =>
    visibleMessages.some(
      (message, index) =>
        index > messageIndex &&
        message.role === 'assistant' &&
        blocksForMessage(index).some((block) => block.type === 'story')
    )

  // Debug data (kept permanently now) only ever exists for admins — its presence on any
  // message doubles as a cheap admin check without threading a separate flag through, and
  // since it can only ever attach to a message that already completed a real turn, it's also
  // sufficient on its own to mean "this chat is ongoing" — no separate content-length check
  // needed. Shown as a running total so a conversation trending toward the server's 10mb save
  // limit is visible before a save silently fails, not just discoverable after the fact.
  const hasDebugData = conversation.messages.some(
    (message) => Array.isArray(message.metadata?.debug) && message.metadata.debug.length > 0
  )
  const conversationSizeInfo = useMemo(() => {
    if (!hasDebugData) return null
    const bytes = new Blob([JSON.stringify(conversation.messages)]).size
    const percent = Math.min(100, Math.round((bytes / CONVERSATION_SIZE_LIMIT_BYTES) * 100))
    const formatted =
      bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    return { percent, formatted }
  }, [conversation.messages, hasDebugData])

  const composerPlaceholder =
    conversation.mode === 'ask'
      ? 'Ask about your wiki, story, or characters…'
      : conversation.mode === 'plan'
        ? 'Describe the scene you want to plan…'
        : 'Ask your story agent… Type @ to mention a wiki page.'

  // A reattached run has no requestId on this client — that died with the page that started
  // it — so handleCancelRequest would return having done nothing. Better no Stop button than
  // one that silently does nothing.
  const isStopShown = isThisConversationLoading && !isReattachedRun

  return (
    <div
      className={`conversation-container flex flex-col h-full ${visibleMessages.length === 0 ? 'conversation-container-empty' : ''}`}
    >
      <div className="chat-header h-12 px-3 border-b border-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-1.5 truncate flex-grow mr-2">
          <h3 className="font-bold text-sm truncate text-foreground">{conversation.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          {/* Same guard as New Chat below: an unstarted conversation has nothing worth
              naming — its title is generated from the first exchange anyway. */}
          {onRename && visibleMessages.length > 0 && (
            <IconButton
              variant="ghost"
              size="sm"
              icon={<PenLine size={14} />}
              label="Rename Chat"
              tooltip="Rename Chat"
              onClick={onRename}
            />
          )}
          {onNewChat && visibleMessages.length > 0 && (
            <IconButton
              variant="ghost"
              size="sm"
              icon={<Plus size={14} />}
              label="New Chat"
              tooltip="New Chat"
              onClick={onNewChat}
            />
          )}
          {onShowHistory && (
            <IconButton
              variant="ghost"
              size="sm"
              icon={<History size={14} />}
              label="Chat History"
              tooltip="Chat History"
              onClick={onShowHistory}
            />
          )}
          {onClose && (
            <IconButton
              variant="ghost"
              size="sm"
              icon={<X size={14} />}
              label="Close Chat Panel"
              tooltip="Close Chat Panel"
              onClick={onClose}
            />
          )}
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onClick={handleChatClick}
        className="chat-messages-scroll flex-grow overflow-y-auto py-4 flex flex-col gap-4"
      >
        <ChatMessageList
          className="chat-message-list-panel"
          isStreaming={isThisConversationLoading}
          emptyState={
            visibleMessages.length === 0 ? (
              <div className="flex-grow flex flex-col justify-center items-center text-center p-6 text-muted-foreground mt-8">
                <Sparkles size={36} className="text-brand opacity-60 mb-2 spin-slow" />
                <h4 className="font-bold text-sm text-foreground mb-1">Co-Writing Chat</h4>
                <p className="text-xs max-w-xs leading-relaxed mb-4">
                  Ask suggestions, outline plans, or query facts. The model automatically scans your
                  Story and Wiki database to maintain context.
                </p>
                <div className="flex flex-col gap-2 w-full max-w-sm">
                  {quickPrompts.map((p, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(p)}
                      className="quick-prompt-btn text-left text-xs p-2 rounded border border-border glassmorphism-card hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : undefined
          }
        >
          {/* The rule walks the call graph of every function named in this subtree and finds a
              ref read, but not *when* the call happens. The three it reaches -- handleCopyText,
              handleReask, handleExecutePlan -- are each only ever invoked from an onClick, so the
              ref is read on interaction, never during render. The one function this subtree does
              call at render time, getSelectionDisplay, is module-level and touches no ref.
              Re-check this if anything here starts calling a component-scoped function directly. */}
          {/* eslint-disable-next-line react-hooks/refs */}
          {visibleMessages.map((msg, messageIndex) => (
            <ChatMessage key={msg.id} sender={msg.role === 'user' ? 'user' : 'assistant'}>
              <ChatMessageBubble
                variant={msg.role === 'user' ? 'filled' : 'ghost'}
                metadata={
                  msg.role === 'user' ? (
                    <ChatMessageMetadata
                      timestamp={
                        <Timestamp value={new Date(msg.timestamp).toISOString()} format="time" />
                      }
                      footer={
                        <div className="inline-flex items-center gap-1">
                          <PillButton
                            variant="ghost"
                            onClick={() =>
                              handleCopyText(
                                getPlanToExecute(msg)
                                  ? `Plan to execute\n\n${getPlanToExecute(msg)}`
                                  : msg.content
                              )
                            }
                            tooltip={getPlanToExecute(msg) ? 'Copy plan' : 'Copy prompt'}
                            label="Copy"
                            isIconOnly
                            icon={<Copy size={10} />}
                            className="chat-bubble-action-btn animate-none opacity-60 hover:opacity-100"
                            style={{
                              padding: 0,
                              width: '20px',
                              height: '20px',
                              border: 'none',
                              background: 'none'
                            }}
                          />
                          <PillButton
                            variant="ghost"
                            onClick={() =>
                              handleReask(
                                msg.content,
                                msg.metadata?.mode,
                                undefined,
                                msg.metadata?.isPlanRevision,
                                getPlanToExecute(msg) ?? undefined
                              )
                            }
                            tooltip="Resend prompt"
                            label="Resend"
                            isIconOnly
                            icon={<RefreshCw size={10} />}
                            className="chat-bubble-action-btn animate-none opacity-60 hover:opacity-100"
                            style={{
                              padding: 0,
                              width: '20px',
                              height: '20px',
                              border: 'none',
                              background: 'none'
                            }}
                            isDisabled={loading}
                          />
                        </div>
                      }
                    />
                  ) : undefined
                }
              >
                {msg.role === 'user'
                  ? (() => {
                      // The writer types their messages as plain text, so a single newline is a
                      // deliberate break rather than markdown soft-wrapping.
                      const renderUserMarkdown = (markdown: string): string =>
                        marked.parse(resolveWikiLinks(markdown), { breaks: true }) as string
                      const storyEdit = msg.metadata?.isStoryEdit
                        ? parseStoryEditRequest(msg.content)
                        : null
                      if (storyEdit) {
                        return (
                          <div
                            className="user-message-content user-message-markdown markdown-content plan-to-execute plan-revision-request"
                            dangerouslySetInnerHTML={{
                              __html: renderUserMarkdown(formatStoryEditMarkdown(storyEdit))
                            }}
                          />
                        )
                      }
                      const planRevision = msg.metadata?.isPlanRevision
                        ? parsePlanRevisionRequest(msg.content)
                        : null
                      if (planRevision) {
                        return (
                          <div
                            className="user-message-content user-message-markdown markdown-content plan-to-execute plan-revision-request"
                            dangerouslySetInnerHTML={{
                              __html: renderUserMarkdown(formatPlanRevisionMarkdown(planRevision))
                            }}
                          />
                        )
                      }
                      const planToExecute = getPlanToExecute(msg)
                      if (planToExecute) {
                        return (
                          <div
                            className="user-message-content user-message-markdown markdown-content plan-to-execute"
                            dangerouslySetInnerHTML={{
                              __html: marked.parse(
                                resolveWikiLinks(`### Plan to execute\n\n${planToExecute}`)
                              ) as string
                            }}
                          />
                        )
                      }
                      const selection = getSelectionDisplay(msg)
                      return selection ? (
                        <div className="user-selection-message">
                          <p>I have highlighted the following text in my story:</p>
                          <blockquote>
                            <div>{selection.text}</div>
                          </blockquote>
                          <p>My instructions:</p>
                          <div
                            className="user-message-content user-message-markdown markdown-content"
                            dangerouslySetInnerHTML={{
                              __html: renderUserMarkdown(selection.instructions)
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className="user-message-content user-message-markdown markdown-content"
                          dangerouslySetInnerHTML={{ __html: renderUserMarkdown(msg.content) }}
                        />
                      )
                    })()
                  : (() => {
                      const cleanedContent = msg.content.replace(/^(\s*\.\.\.\s*)+/, '')
                      const hasLaterUserMessage = messageIndex < latestUserMessageIndex
                      const isThinking =
                        loading && msg.id === activeAssistantMessageId && cleanedContent === ''
                      if (isThinking) {
                        return (
                          <div className="flex flex-col gap-2">
                            <AgentWorkDisclosure
                              key={`${msg.id}-active`}
                              items={msg.metadata?.activity}
                              timing={msg.metadata?.timing}
                              isActive
                              liveElapsedMs={liveElapsedMs}
                              status={agentStatus}
                            />
                          </div>
                        )
                      }
                      if (msg.metadata?.plan) {
                        const plan = msg.metadata.plan
                        return (
                          <div className="flex flex-col gap-3">
                            <AgentWorkDisclosure
                              key={`${msg.id}-${loading && msg.id === activeAssistantMessageId ? 'active' : 'done'}`}
                              items={msg.metadata.activity}
                              timing={msg.metadata.timing}
                              isActive={loading && msg.id === activeAssistantMessageId}
                              liveElapsedMs={liveElapsedMs}
                              status={agentStatus}
                              planSummary={plan.summary}
                            />
                            <div
                              className="user-message-content"
                              style={{ marginBottom: 'var(--spacing-2)' }}
                            >
                              {plan.summary}
                            </div>
                            <HStack gap={1.5}>
                              <PillButton
                                variant="secondary"
                                label="View Plan"
                                onClick={() => setPlanModalMessageId(msg.id)}
                              />
                              {!hasLaterUserMessage && (
                                <PillButton
                                  variant="primary"
                                  label="Execute"
                                  onClick={() => handleExecutePlan(plan.content)}
                                  isDisabled={loading}
                                />
                              )}
                            </HStack>
                            <div className="chat-bubble-actions flex mt-2 border-t pt-2 border-border items-center justify-start gap-3">
                              <div
                                className="flex items-center gap-1 text-muted-foreground"
                                style={{ fontSize: '10.5px' }}
                              >
                                <Check size={11} className="text-green-500" />
                                <span>Done</span>
                                <span className="text-muted-foreground/60 mx-0.5">•</span>
                                <Timestamp
                                  value={new Date(msg.timestamp).toISOString()}
                                  format="time"
                                />
                              </div>
                              {msg.metadata?.debug && msg.metadata.debug.length > 0 && (
                                <IconButton
                                  variant="ghost"
                                  onClick={() => setDebugModalMessageId(msg.id)}
                                  tooltip="Debug info"
                                  label="Debug info"
                                  icon={<Bug size={10} />}
                                  className="chat-bubble-action-btn animate-none"
                                  style={{ padding: 0, width: '20px', height: '20px' }}
                                />
                              )}
                            </div>
                          </div>
                        )
                      }
                      const blocks = parseMessageBlocks(cleanedContent, {
                        mode: modeForMessage(messageIndex)
                      })
                      return (
                        <div className="flex flex-col gap-3">
                          <AgentWorkDisclosure
                            key={`${msg.id}-${loading && msg.id === activeAssistantMessageId ? 'active' : 'done'}`}
                            items={msg.metadata?.activity}
                            timing={msg.metadata?.timing}
                            isActive={loading && msg.id === activeAssistantMessageId}
                            liveElapsedMs={liveElapsedMs}
                            status={agentStatus}
                          />
                          {blocks.map((block, bIdx) => {
                            if (block.type === 'story') {
                              const dropdownId = `${msg.id}-${bIdx}`
                              const isDropdownOpen = openDropdownId === dropdownId
                              const isSuperseded = hasLaterStoryBlock(messageIndex)

                              return (
                                <div key={bIdx} className="story-draft-block">
                                  <div
                                    className="story-draft-content"
                                    dangerouslySetInnerHTML={{
                                      __html: marked.parse(
                                        resolveWikiLinks(block.content)
                                      ) as string
                                    }}
                                  />
                                  <div className="story-draft-footer">
                                    <PillButton
                                      variant="secondary"
                                      onClick={() => handleCopyText(block.content)}
                                      tooltip="Copy only the story draft"
                                      label="Copy"
                                      icon={<Copy size={9} />}
                                      className="h-5 text-[9px] px-2 flex items-center gap-1"
                                    />
                                    <PillButton
                                      variant="secondary"
                                      onClick={() =>
                                        setStoryEditTarget({ messageId: msg.id, blockIndex: bIdx })
                                      }
                                      tooltip={
                                        isSuperseded
                                          ? 'View this passage'
                                          : 'Comment on paragraphs and request changes'
                                      }
                                      label={isSuperseded ? 'View' : 'Edit'}
                                      icon={isSuperseded ? <Eye size={9} /> : <Pencil size={9} />}
                                      isDisabled={loading && !isSuperseded}
                                      className="h-5 text-[9px] px-2 flex items-center gap-1"
                                    />
                                    {!isSuperseded && (
                                      <div className="story-draft-insert-group">
                                        {hasSelection ? (
                                          <PillButton
                                            variant="primary"
                                            onClick={() =>
                                              onInsertIntoStory(block.content, 'replace')
                                            }
                                            tooltip="Replace selected text in canvas"
                                            label="Replace"
                                            icon={<CornerDownLeft size={9} />}
                                            className="story-draft-insert-btn"
                                          />
                                        ) : (
                                          <PillButton
                                            variant="primary"
                                            onClick={() =>
                                              onInsertIntoStory(block.content, 'cursor')
                                            }
                                            tooltip="Insert where the cursor is"
                                            label="Insert"
                                            icon={<CornerDownLeft size={9} />}
                                            className="story-draft-insert-btn"
                                          />
                                        )}
                                        <button
                                          type="button"
                                          className="story-draft-dropdown-trigger"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setOpenDropdownId(isDropdownOpen ? null : dropdownId)
                                          }}
                                        >
                                          <ChevronDown size={8} />
                                        </button>
                                        {isDropdownOpen && (
                                          <div className="story-draft-dropdown-menu absolute bottom-full right-0 mb-1 bg-popover border border-border rounded shadow-lg py-1 min-w-[140px] z-50 text-[10px]">
                                            <button
                                              type="button"
                                              className="w-full text-left px-2.5 py-1.5 hover:bg-accent hover:text-accent-foreground flex items-center gap-1.5"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                onInsertIntoStory(block.content, 'cursor')
                                                setOpenDropdownId(null)
                                              }}
                                            >
                                              <CornerDownLeft size={9} />
                                              Insert at Cursor
                                            </button>
                                            <button
                                              type="button"
                                              className="w-full text-left px-2.5 py-1.5 hover:bg-accent hover:text-accent-foreground flex items-center gap-1.5"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                onInsertIntoStory(block.content, 'append')
                                                setOpenDropdownId(null)
                                              }}
                                            >
                                              <CornerDownRight size={9} />
                                              Insert at End
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            } else {
                              return (
                                <div
                                  key={bIdx}
                                  className="markdown-content"
                                  dangerouslySetInnerHTML={{
                                    __html: marked.parse(resolveWikiLinks(block.content)) as string
                                  }}
                                />
                              )
                            }
                          })}
                          {!isThinking && (
                            <div className="chat-bubble-actions flex mt-2 border-t pt-2 border-border items-center justify-start gap-3">
                              <div
                                className="flex items-center gap-1 text-muted-foreground"
                                style={{ fontSize: '10.5px' }}
                              >
                                <Check size={11} className="text-green-500" />
                                <span>Done</span>
                                <span className="text-muted-foreground/60 mx-0.5">•</span>
                                <Timestamp
                                  value={new Date(msg.timestamp).toISOString()}
                                  format="time"
                                />
                              </div>
                              <PillButton
                                variant="ghost"
                                onClick={() => handleCopyText(cleanedContent)}
                                tooltip="Copy full response"
                                label="Copy"
                                isIconOnly
                                icon={<Copy size={10} />}
                                className="chat-bubble-action-btn animate-none"
                                style={{ padding: 0, width: '20px', height: '20px' }}
                              />
                              {msg.metadata?.debug && msg.metadata.debug.length > 0 && (
                                <IconButton
                                  variant="ghost"
                                  onClick={() => setDebugModalMessageId(msg.id)}
                                  tooltip="Debug info"
                                  label="Debug info"
                                  icon={<Bug size={10} />}
                                  className="chat-bubble-action-btn animate-none"
                                  style={{ padding: 0, width: '20px', height: '20px' }}
                                />
                              )}
                            </div>
                          )}
                          {/* Answers to a clarifying question the agent asked. Not the editorial
                              "what next" suggestions, which were removed. */}
                          {!hasLaterUserMessage &&
                            !loading &&
                            msg.metadata?.clarifyActions &&
                            msg.metadata.clarifyActions.length > 0 && (
                              <HStack gap={1.5} paddingBlock={3} wrap="wrap">
                                {msg.metadata.clarifyActions.map((action, actionIndex) => (
                                  <PillButton
                                    key={`${action.type}-${actionIndex}`}
                                    variant="secondary"
                                    label={action.label}
                                    onClick={() => handleReask(action.prompt)}
                                    isDisabled={loading}
                                    className="agent-clarify-button text-xs"
                                  />
                                ))}
                              </HStack>
                            )}
                        </div>
                      )
                    })()}
              </ChatMessageBubble>
            </ChatMessage>
          ))}

          {error && (
            <div className="p-3 rounded-lg text-xs flex flex-col gap-2 border bg-destructive/10 border-destructive/20 text-destructive shrink-0">
              <div className="flex gap-2 items-start">
                <AlertCircle size={14} className="shrink-0 text-destructive mt-0.5" />
                <span className="flex-grow leading-normal">{error}</span>
              </div>
              {lastAttemptMessages && !loading && (
                <div className="flex justify-end mt-1 border-t border-destructive/10 pt-2">
                  <PillButton
                    variant="secondary"
                    onClick={() => startAICompletion(lastAttemptMessages)}
                    label="Retry"
                    icon={<RefreshCw size={10} />}
                    className="h-6 text-[10px] px-2.5 flex items-center gap-1 border-destructive/20 text-destructive hover:bg-destructive/10"
                  />
                </div>
              )}
            </div>
          )}
        </ChatMessageList>
        <div ref={chatEndRef} className="h-8 shrink-0" />
      </div>

      {planModalMessageId &&
        (() => {
          const planMessageIndex = conversation.messages.findIndex(
            (message) => message.id === planModalMessageId
          )
          const planMessage = conversation.messages[planMessageIndex]
          if (!planMessage?.metadata?.plan) return null
          const plan = planMessage.metadata.plan
          const previousPlanMessage = conversation.messages
            .slice(0, planMessageIndex)
            .reverse()
            .find((message) => message.metadata?.plan)
          // A plan is stale once a later plan supersedes it — commenting/editing it
          // any further can't do anything, since the conversation has moved on.
          const isStale = conversation.messages
            .slice(planMessageIndex + 1)
            .some((message) => message.metadata?.plan)
          // A chat started from the selection bubble opens on a user message carrying that
          // selection; the plan rewrites it, so the modal shows it above the beats.
          const originSelection = conversation.messages
            .map((message) => (message.role === 'user' ? getSelectionDisplay(message) : null))
            .find((selection) => selection !== null)
          return (
            <PlanModal
              plan={plan}
              previousPlanContent={previousPlanMessage?.metadata?.plan?.content}
              selectedText={originSelection?.text}
              isReadOnly={isStale}
              onCommentsChange={(comments) => handleSetPlanComments(planMessage.id, comments)}
              onRequestChanges={(comments) =>
                // handleRequestPlanChanges reaches a ref through handleReask, so the rule flags
                // it being named here. This is the modal's change-request callback though: it
                // runs when the writer submits comments, not while this renders.
                // eslint-disable-next-line react-hooks/refs
                handleRequestPlanChanges(planMessage.id, plan.content, comments)
              }
              onClose={() => setPlanModalMessageId(null)}
            />
          )
        })()}

      {storyEditTarget &&
        (() => {
          const message = conversation.messages.find(
            (item) => item.id === storyEditTarget.messageId
          )
          if (!message) return null
          const messageIndex = visibleMessages.findIndex((item) => item.id === message.id)
          if (messageIndex === -1) return null
          const blocks = blocksForMessage(messageIndex)
          const block = blocks[storyEditTarget.blockIndex]
          if (!block || block.type !== 'story') return null
          const state = message.metadata?.storyEdits?.[storyEditTarget.blockIndex]
          const isStale = hasLaterStoryBlock(messageIndex)
          return (
            <StoryEditModal
              key={`${message.id}-${storyEditTarget.blockIndex}`}
              content={block.content}
              comments={state?.comments}
              previousContent={state?.previousContent}
              isReadOnly={isStale}
              onCommentsChange={(comments) =>
                handleSetStoryEditComments(message.id, storyEditTarget.blockIndex, comments)
              }
              onRequestChanges={(comments) =>
                // eslint-disable-next-line react-hooks/refs
                handleRequestStoryEdits(
                  message.id,
                  storyEditTarget.blockIndex,
                  block.content,
                  comments
                )
              }
              onClose={() => setStoryEditTarget(null)}
            />
          )
        })()}

      {debugModalMessageId &&
        (() => {
          const debugMessage = conversation.messages.find(
            (message) => message.id === debugModalMessageId
          )
          if (!debugMessage?.metadata?.debug) return null
          return (
            <DebugModal
              conversationId={conversation.id}
              entries={debugMessage.metadata.debug}
              onClose={() => setDebugModalMessageId(null)}
            />
          )
        })()}

      {/* Composer Input */}
      <div className="chat-composer-shell p-3 border-t shrink-0">
        {visibleMessages.length === 0 && (
          <Heading level={3} justify="center">
            What would you like to work on?
          </Heading>
        )}
        <ChatComposer
          value={input}
          onChange={setInput}
          onSubmit={handleSend}
          onStop={() => void handleCancelRequest()}
          isStopShown={isStopShown}
          status={
            lockedByOther && busyConversationId
              ? { type: 'warning', message: "You're limited to one conversation at a time." }
              : undefined
          }
          // ChatComposer's disabled state applies pointer-events:none to the entire shell,
          // including its otherwise-enabled Stop button. Disable only the input below while a
          // request is active so Stop remains reachable.
          placeholder={composerPlaceholder}
          input={
            <ChatComposerInput
              value={input}
              onChange={setInput}
              triggers={[guideMentionTrigger]}
              isDisabled={loading || lockedByOther}
              placeholder={composerPlaceholder}
            />
          }
        />
        {lockedByOther && busyConversationId && (
          <div className="conversation-busy-goto-row">
            {'Go to '}
            <button
              type="button"
              className="conversation-busy-goto"
              onClick={() => onOpenConversation?.(busyConversationId)}
            >
              Active Conversation
            </button>
          </div>
        )}
        <div className="chat-composer-mode-row">
          <Text type="label" color="secondary">
            Mode
          </Text>
          <SegmentedControl
            label="Conversation mode"
            size="sm"
            value={conversation.mode}
            onChange={(value) =>
              onSetConversationMode(conversation.id, value as 'plan' | 'ask' | 'writing')
            }
          >
            <SegmentedControlItem value="writing" label="Writing" icon={<PenLine size={12} />} />
            <SegmentedControlItem
              value="ask"
              label="Ask"
              icon={<MessageCircleQuestion size={12} />}
            />
            <SegmentedControlItem value="plan" label="Plan" icon={<ListTree size={12} />} />
          </SegmentedControl>
          {conversationSizeInfo && (
            <ConversationSizeMeter
              percent={conversationSizeInfo.percent}
              formatted={conversationSizeInfo.formatted}
            />
          )}
        </div>
      </div>
    </div>
  )
}
